/**
 * Severity, rule-family and year facets over the findings list.
 *
 * Counts are computed against every OTHER facet's current selection, so
 * ticking "errors" does not drop every warning chip to zero — the reader can
 * still see that switching would surface eleven more findings. A chip whose
 * count is zero under the current selection is disabled, not hidden: hiding it
 * removes the very affordance that would let the reader discover it.
 */

import FilterChip from '../../ui/FilterChip';
import type { IssueSeverity, ValidationIssue } from '../../../types/dataQuality';
import type { IssueFilter } from './dqSelectors';
import { issueFacets } from './dqSelectors';
import { FAMILY_ORDER, familyHint, familyLabel, type RuleFamily } from './dqRules';
import { SEVERITY_TOKENS, verdictToken } from './dqTokens';

export interface FindingsFacetRailProps {
  issues: ValidationIssue[];
  filter: IssueFilter;
  onToggleSeverity: (severity: IssueSeverity) => void;
  onToggleFamily: (family: RuleFamily) => void;
  onToggleYear: (year: number) => void;
  onToggleVerdict: (verdict: string) => void;
  onClear: () => void;
  active: boolean;
}

function verdictLabel(verdict: string): string {
  return verdictToken(verdict)?.label ?? verdict;
}

export default function FindingsFacetRail({
  issues,
  filter,
  onToggleSeverity,
  onToggleFamily,
  onToggleYear,
  onToggleVerdict,
  onClear,
  active,
}: FindingsFacetRailProps) {
  const facets = issueFacets(issues, filter, verdictLabel);

  // Stable display order. Set-insertion order made the chips reshuffle between
  // refetches, which is disorienting in a control the reader is aiming at.
  const families = [...facets.family].sort(
    (a, b) => FAMILY_ORDER.indexOf(a.value) - FAMILY_ORDER.indexOf(b.value)
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-widest text-neutral-400">Filter</p>
        {active && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs font-medium text-primary-600 hover:text-primary-800"
          >
            Clear all
          </button>
        )}
      </div>

      {facets.severity.length > 0 && (
        <div>
          <p className="mb-1.5 text-[11px] uppercase tracking-wider text-neutral-400">
            Severity
          </p>
          <div className="flex flex-wrap gap-1.5">
            {facets.severity.map((option) => (
              <FilterChip
                key={option.value}
                label={option.label}
                count={option.count}
                active={option.selected}
                dot={SEVERITY_TOKENS[option.value].dot}
                onToggle={() => onToggleSeverity(option.value)}
              />
            ))}
          </div>
        </div>
      )}

      {families.length > 0 && (
        <div>
          <p className="mb-1.5 text-[11px] uppercase tracking-wider text-neutral-400">
            Kind of check
          </p>
          <div className="flex flex-wrap gap-1.5">
            {families.map((option) => (
              <FilterChip
                key={option.value}
                label={familyLabel(option.value)}
                title={familyHint(option.value)}
                count={option.count}
                active={option.selected}
                onToggle={() => onToggleFamily(option.value)}
              />
            ))}
          </div>
        </div>
      )}

      {facets.years.length > 0 && (
        <div>
          <p className="mb-1.5 text-[11px] uppercase tracking-wider text-neutral-400">
            Fiscal year
          </p>
          <div className="flex flex-wrap gap-1.5">
            {facets.years.map((option) => (
              <FilterChip
                key={option.value}
                label={option.label}
                count={option.count}
                active={option.selected}
                onToggle={() => onToggleYear(option.value)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Only appears once a reviewer has actually recorded verdicts — on a
          run with no AI opinions there is nothing here to filter by. */}
      {facets.verdicts.length > 0 && (
        <div>
          <p className="mb-1.5 text-[11px] uppercase tracking-wider text-neutral-400">
            AI verdict
          </p>
          <div className="flex flex-wrap gap-1.5">
            {facets.verdicts.map((option) => (
              <FilterChip
                key={option.value}
                label={option.label}
                title={verdictToken(option.value)?.effect}
                dot={verdictToken(option.value)?.dot}
                count={option.count}
                active={option.selected}
                onToggle={() => onToggleVerdict(option.value)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
